import { Fragment, useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import {
  formatFncoAmount,
  toFncoPreMarketingView,
  type FncoMarketingAnalysis,
  type FncoPreMarketingView,
} from '@/utils/approval/fncoPreMarketing';
import { FNCO_PRE_MARKETING_LEGACY_CSS } from './legacyCss';
import { FncoPreMarketingRoot } from './styles';

type ReportProps = {
  view: FncoPreMarketingView | null;
  t: TFunction;
};

/** 레거시 #apv_fnco_pre_mkt_content 마크업(id·class 동일) */
const FncoPreMarketingReport = ({ view, t }: ReportProps) => {
  const won = (n: number) => t('{{amount}}원', { amount: formatFncoAmount(n) });
  return (
    <div className="apv_fnco_pre_mkt_case pt-10 pb-10">
      <div className="detail-section">
        <div className="info-grid">
          <div className="info-item">
            <div className="info-label">{t('실행 기간')}</div>
            <div className="info-value">
              <span id="value_dateRange_0">{view?.startDate}</span> ~{' '}
              <span id="value_dateRange_1">{view?.endDate}</span>
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('목적')}</div>
            <div className="info-value" id="value_purpose">
              {view?.purpose ?? t('(기안 목적 미기재)')}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('내용')}</div>
            <div className="info-value content" id="value_summary">
              {view?.summary}
            </div>
          </div>
        </div>

        <hr className="divider" />

        <div className="budget-summary">
          <div className="section-title">{t('사용 예정 내역')}</div>
          <div className="total-amount">
            <span>{t('총')}</span>{' '}
            <span className="amount" id="totalCost">
              {view ? formatFncoAmount(view.totalCost) : ''}
            </span>{' '}
            <span>{t('원')}</span>
          </div>
        </div>

        <div className="table-container">
          <table className="budget-table table table-fixed">
            <tbody>
              {view?.groups.map((group, gi) => {
                const isLastGroup = gi === view.groups.length - 1;
                return (
                  <Fragment key={`${group.category}-${gi}`}>
                    <tr className="category-header">
                      <td>
                        <span style={{ color: 'var(--dk-accent, #0066cc)' }}>{gi + 1}</span>
                      </td>
                      <td colSpan={3}>{group.category}</td>
                      <td className="amount-cell">{won(group.total)}</td>
                    </tr>
                    {group.rows.map((row, ri) => {
                      // 다음 카테고리 헤더의 윗선만 보이게(레거시 동일)
                      const cellStyle =
                        !isLastGroup && ri === group.rows.length - 1
                          ? { borderBottom: 'none' }
                          : undefined;
                      return (
                        <tr key={ri}>
                          <td style={cellStyle}>{ri + 1}</td>
                          <td style={cellStyle}>{row.adType}</td>
                          <td style={cellStyle}>{row.landingMedia}</td>
                          <td style={cellStyle} />
                          <td className="amount-cell" style={cellStyle}>
                            {won(row.amount)}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/** 작성 화면 — 분석 레코드로 그림 */
const FncoPreMarketingInfo = ({
  analysis,
}: {
  analysis: FncoMarketingAnalysis | null;
}) => {
  const { t } = useTranslation();
  return (
    <FncoPreMarketingRoot>
      <FncoPreMarketingReport view={toFncoPreMarketingView(analysis)} t={t} />
    </FncoPreMarketingRoot>
  );
};

const DROP_TAGS = new Set(['style', 'script', 'link']);

/** 조회 화면 — 결재된 내용 그대로 저장 content(레거시 문서 포함)로 그림 */
export const FncoPreMarketingSnapshot = ({ content }: { content: string }) => {
  const transform = useCallback<ElementTransform>(
    ({ tagName }) => (DROP_TAGS.has(tagName) ? null : undefined),
    [],
  );
  const rendered = useHtmlToReact(content, { transform });
  return <FncoPreMarketingRoot>{rendered}</FncoPreMarketingRoot>;
};

/**
 * attributes.content — 레거시 #apv_fnco_pre_mkt_content.innerHTML 과 같은 모양(<style> 포함).
 * 레거시 조회 화면은 이 HTML 만 그대로 출력
 */
export const buildFncoPreMarketingContentHtml = (
  analysis: FncoMarketingAnalysis | null,
  t: TFunction,
): string => {
  const view = toFncoPreMarketingView(analysis);
  if (!view) return '';
  return `<style>${FNCO_PRE_MARKETING_LEGACY_CSS}</style>${renderToStaticMarkup(
    <FncoPreMarketingReport view={view} t={t} />,
  )}`;
};

export default FncoPreMarketingInfo;
